/**
 * 簡易リンタ: 全スクリプトファイル + manifest を走査して問題を列挙する。
 *
 * 検出:
 *  - parse エラー(構文)
 *  - manifest に存在しないキャラ / 背景 / BGM / SE への参照
 *  - 存在しないラベルへの @jump / @call / 選択肢
 *  - 使用されていないラベル(warning)
 */

import { parse, type AstNode, type CommandNode } from "@pix/script";
import type { ProjectManifest } from "@pix/shared";

export type IssueSeverity = "error" | "warning" | "info";

export interface LintIssue {
  file: string;
  line: number;
  severity: IssueSeverity;
  message: string;
  code: string;
}

export interface LintResult {
  issues: LintIssue[];
  /** ファイル別の集計(タブのバッジ用) */
  perFile: Record<string, number>;
}

export function lintProject(
  scripts: Record<string, string>,
  manifest: ProjectManifest,
): LintResult {
  const issues: LintIssue[] = [];

  // 全ラベルを先に集める(file#label)
  const labels = new Set<string>();
  const labelFile: Record<string, string> = {}; // label -> first declaring file
  for (const [file, source] of Object.entries(scripts)) {
    try {
      const f = parse(source, file);
      for (const node of f.nodes) {
        collectLabels(node, file, labels, labelFile);
      }
    } catch {
      /* parse error は後で個別に拾う */
    }
  }

  const usedLabels = new Set<string>();

  for (const [file, source] of Object.entries(scripts)) {
    try {
      const f = parse(source, file);
      for (const node of f.nodes) {
        walk(node, {
          file,
          manifest,
          labels,
          usedLabels,
          push: (issue) => issues.push(issue),
        });
      }
    } catch (e) {
      const msg = (e as Error).message;
      const m = msg.match(/line (\d+)/i);
      issues.push({
        file,
        line: m ? Number(m[1]) : 1,
        severity: "error",
        message: `構文エラー: ${msg}`,
        code: "parse",
      });
    }
  }

  // 未使用ラベル(warning)
  for (const fullName of labels) {
    if (!usedLabels.has(fullName)) {
      const [file, label] = fullName.split("#");
      // entry のラベルは未使用警告から除外
      if (label === "start") continue;
      issues.push({
        file: file!,
        line: 1, // 行は持っていないので 1 で OK(将来 labelFile に line も持たせる)
        severity: "info",
        message: `未使用ラベル: #${label}`,
        code: "unused-label",
      });
    }
  }

  // 集計
  const perFile: Record<string, number> = {};
  for (const it of issues) {
    perFile[it.file] = (perFile[it.file] ?? 0) + 1;
  }

  return { issues, perFile };
}

function collectLabels(
  node: AstNode,
  file: string,
  labels: Set<string>,
  labelFile: Record<string, string>,
): void {
  if (node.type === "Label") {
    const id = `${file}#${node.name}`;
    labels.add(id);
    if (!labelFile[id]) labelFile[id] = file;
  } else if (node.type === "If") {
    for (const br of node.branches) for (const c of br.body) collectLabels(c, file, labels, labelFile);
    if (node.elseBody) for (const c of node.elseBody) collectLabels(c, file, labels, labelFile);
  } else if (node.type === "For") {
    for (const c of node.body) collectLabels(c, file, labels, labelFile);
  } else if (node.type === "Macro") {
    for (const c of node.body) collectLabels(c, file, labels, labelFile);
  }
}

interface Ctx {
  file: string;
  manifest: ProjectManifest;
  labels: Set<string>;
  usedLabels: Set<string>;
  push: (i: LintIssue) => void;
}

function walk(node: AstNode, ctx: Ctx): void {
  if (node.type === "Command") {
    checkCommand(node, ctx);
  } else if (node.type === "Choice") {
    for (const opt of node.options) {
      const f = opt.targetFile ?? ctx.file;
      const lbl = opt.targetLabel;
      const id = `${f}#${lbl}`;
      ctx.usedLabels.add(id);
      if (!ctx.labels.has(id)) {
        ctx.push({
          file: ctx.file,
          line: node.line,
          severity: "error",
          message: `選択肢ジャンプ先が未定義: ${f === ctx.file ? "" : f + "#"}${lbl}`,
          code: "undef-label",
        });
      }
    }
  } else if (node.type === "If") {
    for (const br of node.branches) for (const c of br.body) walk(c, ctx);
    if (node.elseBody) for (const c of node.elseBody) walk(c, ctx);
  } else if (node.type === "For") {
    for (const c of node.body) walk(c, ctx);
  } else if (node.type === "Macro") {
    for (const c of node.body) walk(c, ctx);
  }
}

function checkCommand(node: CommandNode, ctx: Ctx): void {
  const { file, manifest } = ctx;
  const a = node.args;
  switch (node.name) {
    case "chara": {
      const id = String(a["id"] ?? "");
      if (!id) return;
      if (!manifest.characters?.[id]) {
        ctx.push({
          file,
          line: node.line,
          severity: "error",
          message: `未定義キャラ: id=${id}`,
          code: "undef-chara",
        });
      } else {
        const expr = String(a["face"] ?? a["expression"] ?? "");
        if (expr && !manifest.characters[id]!.expressions?.includes(expr)) {
          ctx.push({
            file,
            line: node.line,
            severity: "warning",
            message: `キャラ ${id} に表情 "${expr}" が未登録`,
            code: "undef-expression",
          });
        }
      }
      break;
    }
    case "bg": {
      if (node.subcommand !== "show") return;
      const name = String(a["name"] ?? "");
      if (name && !manifest.backgrounds?.[name]) {
        ctx.push({
          file,
          line: node.line,
          severity: "error",
          message: `未定義背景: name=${name}`,
          code: "undef-bg",
        });
      }
      break;
    }
    case "bgm": {
      if (node.subcommand !== "play") return;
      const fileKey = String(a["file"] ?? a["play"] ?? "");
      if (
        fileKey &&
        fileKey !== "__volume__" &&
        !manifest.audio?.bgm?.[fileKey]
      ) {
        ctx.push({
          file,
          line: node.line,
          severity: "error",
          message: `未定義 BGM: file=${fileKey}`,
          code: "undef-bgm",
        });
      }
      break;
    }
    case "se": {
      if (node.subcommand !== "play") return;
      const fileKey = String(a["file"] ?? "");
      if (fileKey && !manifest.audio?.se?.[fileKey]) {
        ctx.push({
          file,
          line: node.line,
          severity: "error",
          message: `未定義 SE: file=${fileKey}`,
          code: "undef-se",
        });
      }
      break;
    }
    case "jump":
    case "call": {
      const raw = String(a["_raw"] ?? "").trim();
      if (!raw) return;
      let tFile = ctx.file;
      let tLabel = raw;
      if (raw.startsWith("#")) {
        tLabel = raw.slice(1);
      } else if (raw.includes("#")) {
        const idx = raw.indexOf("#");
        tFile = raw.slice(0, idx);
        tLabel = raw.slice(idx + 1);
      }
      const id = `${tFile}#${tLabel}`;
      ctx.usedLabels.add(id);
      if (!ctx.labels.has(id)) {
        ctx.push({
          file: ctx.file,
          line: node.line,
          severity: "error",
          message: `${node.name === "call" ? "コール" : "ジャンプ"}先が未定義: ${raw}`,
          code: "undef-label",
        });
      }
      break;
    }
  }
}
